import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  consultationBookingService,
  LeadConsultationBookingResponse,
} from "../services/consultationBookingService";

interface LeadIdentity {
  leadNumber: string;
  firstName: string;
  lastName: string;
  email: string;
}

interface BookingForm {
  date: string;
  slot: string;
}

const TIME_SLOTS = [
  { value: "09:00", label: "09:00 AM" },
  { value: "11:00", label: "11:00 AM" },
  { value: "14:00", label: "02:00 PM" },
  { value: "16:00", label: "04:00 PM" },
];

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const isUuid = (value: string): boolean => UUID_PATTERN.test(value.trim());

const extractApiError = (error: unknown, fallback: string): string => {
  if (
    typeof error === "object" &&
    error !== null &&
    "response" in error &&
    typeof (error as { response?: { data?: { detail?: string } } }).response?.data?.detail ===
      "string"
  ) {
    return (error as { response?: { data?: { detail?: string } } }).response?.data?.detail ?? fallback;
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallback;
};

const firstQueryValue = (searchParams: URLSearchParams, keys: string[]): string => {
  for (const key of keys) {
    const value = searchParams.get(key)?.trim();
    if (value) return value;
  }
  return "";
};

const formatDateLabel = (value: string | null): string => {
  if (!value) return "Not booked";
  const parsed = new Date(`${value}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
};

const formatSlotLabel = (value: string | null): string => {
  if (!value) return "";
  const configured = TIME_SLOTS.find((slot) => slot.value === value);
  if (configured) return configured.label;

  const normalized = value.trim();
  if (!/^\d{2}:\d{2}$/.test(normalized)) return normalized;

  const [rawHour, minute] = normalized.split(":");
  const hour = Number(rawHour);
  if (Number.isNaN(hour)) return normalized;
  const suffix = hour >= 12 ? "PM" : "AM";
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${hour12.toString().padStart(2, "0")}:${minute} ${suffix}`;
};

const formatBookedSchedule = (dateValue: string | null, slotValue: string | null): string => {
  if (!dateValue) return "Not booked";
  const dateLabel = formatDateLabel(dateValue);
  const slotLabel = formatSlotLabel(slotValue);
  return slotLabel ? `${dateLabel}, ${slotLabel}` : dateLabel;
};

const toDateInputValue = (value: string | null): string => {
  if (!value) return "";
  if (/^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";

  const year = parsed.getFullYear();
  const month = `${parsed.getMonth() + 1}`.padStart(2, "0");
  const day = `${parsed.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getLocalDateInputValue = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = `${now.getMonth() + 1}`.padStart(2, "0");
  const day = `${now.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const hasConsultationHappened = (lead: LeadConsultationBookingResponse): boolean => {
  return Boolean(
    (lead.qualification_outcome ?? "").trim() ||
      (lead.consultation_notes ?? "").trim()
  );
};

const ConsultationBookingPage = () => {
  const [searchParams] = useSearchParams();

  const leadId = useMemo(
    () => firstQueryValue(searchParams, ["lead_id", "leadId"]),
    [searchParams]
  );
  const hasLeadId = leadId.length > 0;
  const hasValidLeadId = isUuid(leadId);

  const initialLeadIdentity = useMemo<LeadIdentity>(
    () => ({
      leadNumber: firstQueryValue(searchParams, ["lead_number", "leadNumber"]),
      firstName: firstQueryValue(searchParams, ["first_name", "firstName"]),
      lastName: firstQueryValue(searchParams, ["last_name", "lastName"]),
      email: firstQueryValue(searchParams, ["email"]),
    }),
    [searchParams]
  );

  const [leadIdentity, setLeadIdentity] = useState<LeadIdentity>(initialLeadIdentity);
  const [bookingForm, setBookingForm] = useState<BookingForm>({
    date: "",
    slot: TIME_SLOTS[0].value,
  });
  const [existingBookingDate, setExistingBookingDate] = useState<string | null>(null);
  const [existingBookingSlot, setExistingBookingSlot] = useState<string | null>(null);
  const [consultationCompleted, setConsultationCompleted] = useState(false);
  const [editingBooking, setEditingBooking] = useState(true);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState<string | null>(null);

  const hydrateFromApi = useCallback(
    (response: LeadConsultationBookingResponse) => {
      setLeadIdentity((prev) => ({
        leadNumber: response.lead_number || prev.leadNumber || initialLeadIdentity.leadNumber,
        firstName: response.first_name || prev.firstName || initialLeadIdentity.firstName,
        lastName: response.last_name || prev.lastName || initialLeadIdentity.lastName,
        email: response.email || prev.email || initialLeadIdentity.email,
      }));
      setExistingBookingDate(response.consultation_date);
      setExistingBookingSlot(response.consultation_time_slot);
      setConsultationCompleted(hasConsultationHappened(response));

      if (response.consultation_date) {
        const dateFromApi = toDateInputValue(response.consultation_date);
        const slotFromApi = response.consultation_time_slot?.trim() || "";
        setBookingForm((prev) => ({
          date: dateFromApi || prev.date,
          slot: slotFromApi || prev.slot,
        }));
        setEditingBooking(false);
      } else {
        setEditingBooking(true);
      }
    },
    [initialLeadIdentity.email, initialLeadIdentity.firstName, initialLeadIdentity.lastName, initialLeadIdentity.leadNumber]
  );

  const fetchBooking = useCallback(async () => {
    if (!hasValidLeadId) return;

    setLoading(true);
    setLoadError(null);
    try {
      const response = await consultationBookingService.getLeadConsultationBooking(leadId);
      hydrateFromApi(response);
    } catch (error) {
      setLoadError(extractApiError(error, "Could not load lead booking details."));
    } finally {
      setLoading(false);
    }
  }, [hasValidLeadId, hydrateFromApi, leadId]);

  useEffect(() => {
    void fetchBooking();
  }, [fetchBooking]);

  const fullName = `${leadIdentity.firstName} ${leadIdentity.lastName}`.trim();
  const bookedDateTimeLabel = formatBookedSchedule(existingBookingDate, existingBookingSlot);
  const minSelectableDate = useMemo(() => getLocalDateInputValue(), []);
  const selectedBookingDateTime = bookingForm.date
    ? `${bookingForm.date} • ${formatSlotLabel(bookingForm.slot)}`
    : "Select date and slot";
  const selectedSlotLabel =
    TIME_SLOTS.find((slot) => slot.value === bookingForm.slot)?.label ?? bookingForm.slot;
  const canRechooseSlot = Boolean(existingBookingDate) && !consultationCompleted;

  const isSaveDisabled =
    !hasValidLeadId || loading || saving || !editingBooking || !bookingForm.date || !bookingForm.slot;

  const updateDate = (value: string) => {
    setBookingForm((prev) => ({ ...prev, date: value }));
    if (submitError) setSubmitError(null);
    if (submitSuccess) setSubmitSuccess(null);
  };

  const updateSlot = (value: string) => {
    setBookingForm((prev) => ({ ...prev, slot: value }));
    if (submitError) setSubmitError(null);
    if (submitSuccess) setSubmitSuccess(null);
  };

  const handleSaveBooking = async () => {
    if (!hasLeadId) {
      setSubmitError("Missing lead_id query parameter.");
      return;
    }
    if (!hasValidLeadId) {
      setSubmitError("lead_id query parameter must be a valid UUID.");
      return;
    }
    if (!bookingForm.date || !bookingForm.slot) {
      setSubmitError("Please select both date and time slot.");
      return;
    }
    if (consultationCompleted) {
      setSubmitError("Consultation has already happened. Slot cannot be changed.");
      return;
    }

    setSaving(true);
    setSubmitError(null);
    setSubmitSuccess(null);
    try {
      await consultationBookingService.saveLeadConsultationBooking(leadId, {
        consultation_date: bookingForm.date,
        consultation_time_slot: bookingForm.slot,
      });
      await fetchBooking();
      setSubmitSuccess("Consultation slot has been saved.");
    } catch (error) {
      const message = extractApiError(error, "Could not save consultation booking.");
      setSubmitError(message);
      if (message.toLowerCase().includes("already")) {
        await fetchBooking();
      }
    } finally {
      setSaving(false);
    }
  };

  const handleRechoose = () => {
    if (!canRechooseSlot) return;
    setSubmitError(null);
    setSubmitSuccess(null);
    setEditingBooking(true);
  };

  return (
    <div className="login-page">
      <div className="login-shell">
        <aside className="login-brand card">
          <div className="logo" role="img" aria-label="CaseFlow" />
          <span className="badge badge-teal login-badge">
            <i className="ti ti-calendar-time" /> Consultation booking
          </span>
          <div className="login-brand-copy">
            <h1 className="login-title">Book Your Consultation Slot</h1>
            <p className="login-subtitle">
              Pick a preferred date and time slot. Your booking will be saved against your lead.
            </p>
          </div>

          <div className="login-points">
            <div className="login-point">
              <i className="ti ti-clock-hour-4" />
              <div>
                <div className="login-point-title">Pick a slot</div>
                <div className="login-point-sub">
                  Select a date and available consultation time.
                </div>
              </div>
            </div>
            <div className="login-point">
              <i className="ti ti-device-floppy" />
              <div>
                <div className="login-point-title">Save once</div>
                <div className="login-point-sub">
                  Your selected slot is linked to your lead ID for future visits.
                </div>
              </div>
            </div>
            <div className="login-point">
              <i className="ti ti-refresh-dot" />
              <div>
                <div className="login-point-title">Re-choose if needed</div>
                <div className="login-point-sub">
                  You can re-choose the slot until consultation is completed.
                </div>
              </div>
            </div>
          </div>
        </aside>

        <section className="login-form-card card">
          <div className="card-hd">
            <i className="ti ti-calendar-event" /> Consultation booking
          </div>
          <p className="login-form-copy">
            Select a consultation date and time slot, then click
            {" "}
            <strong>Book consultation</strong>.
          </p>

          {!hasLeadId || !hasValidLeadId ? (
            <section className="notice notice-red">
              <i className="ti ti-alert-triangle" />
              <span>
                {!hasLeadId
                  ? "Missing lead_id query param. Use /consultation-booking?lead_id=<lead-uuid>."
                  : "Invalid lead_id query param. It must be a valid UUID."}
              </span>
            </section>
          ) : null}

          {loadError ? (
            <section className="notice notice-red">
              <i className="ti ti-alert-triangle" />
              <span>{loadError}</span>
            </section>
          ) : null}

          {submitError ? (
            <section className="notice notice-red">
              <i className="ti ti-alert-triangle" />
              <span>{submitError}</span>
            </section>
          ) : null}

          {submitSuccess ? (
            <section className="notice notice-green">
              <i className="ti ti-check" />
              <span>{submitSuccess}</span>
            </section>
          ) : null}

          {existingBookingDate ? (
            <section className="notice notice-blue">
              <i className="ti ti-calendar-time" />
              <span>
                Consultation currently booked for
                {" "}
                <strong>{bookedDateTimeLabel}</strong>.
              </span>
            </section>
          ) : null}

          {consultationCompleted ? (
            <section className="notice notice-amber">
              <i className="ti ti-lock" />
              <span>
                Consultation appears completed, so the slot cannot be changed.
              </span>
            </section>
          ) : null}

          <section className="login-role-meta">
            <strong>Lead details</strong>
            <p>
              Name: <strong>{fullName || "Not provided"}</strong>
            </p>
            <p>
              Email: <strong>{leadIdentity.email || "Not provided"}</strong>
            </p>
            <p>
              Lead number: <strong>{leadIdentity.leadNumber || "Not provided"}</strong>
            </p>
            <p>
              Lead ID: <strong>{leadId || "Not provided"}</strong>
            </p>
          </section>

          {!editingBooking && canRechooseSlot ? (
            <div className="assistant-modal-actions">
              <button className="btn btn-sm" type="button" onClick={handleRechoose}>
                <i className="ti ti-refresh" />
                Re-choose slot
              </button>
            </div>
          ) : null}

          {editingBooking ? (
            <form
              className="signup-form"
              onSubmit={(event) => {
                event.preventDefault();
                void handleSaveBooking();
              }}
            >
              <div className="field">
                <label htmlFor="consultation-booking-date">Consultation date *</label>
                <input
                  id="consultation-booking-date"
                  type="date"
                  value={bookingForm.date}
                  min={minSelectableDate}
                  disabled={loading || consultationCompleted}
                  onChange={(event) => updateDate(event.target.value)}
                />
              </div>

              <div className="field">
                <label>Time slot *</label>
                <div className="booking-slot-grid">
                  {TIME_SLOTS.map((slot) => (
                    <button
                      key={slot.value}
                      type="button"
                      className={`btn booking-slot-btn ${
                        bookingForm.slot === slot.value ? "active" : ""
                      }`}
                      disabled={loading || consultationCompleted}
                      onClick={() => updateSlot(slot.value)}
                    >
                      <i className="ti ti-clock" />
                      {slot.label}
                    </button>
                  ))}
                </div>
              </div>

              <section className="notice notice-blue">
                <i className="ti ti-info-circle" />
                <span>
                  Selected slot:
                  {" "}
                  <strong>
                    {bookingForm.date ? `${bookingForm.date} • ${selectedSlotLabel}` : selectedBookingDateTime}
                  </strong>
                </span>
              </section>

              <div className="assistant-modal-actions">
                <button className="btn btn-primary btn-sm" type="submit" disabled={isSaveDisabled}>
                  <i className="ti ti-calendar-check" />
                  {saving ? "Booking..." : existingBookingDate ? "Save new slot" : "Book consultation"}
                </button>
              </div>
            </form>
          ) : null}
        </section>
      </div>
    </div>
  );
};

export default ConsultationBookingPage;
